import sha256 from 'crypto-js/sha256';

/**
 * Gen2+ digest authentication uses a fixed username.
 * @see https://shelly-api-docs.shelly.cloud/gen2/General/Authentication
 */
export const DEVICE_AUTH_USER = 'admin';

/**
 * Parses the `realm` out of a device `WWW-Authenticate` header string.
 * The realm for a Shelly Gen2+ device equals its device id (e.g. `shellyplusplugs-441793d0be70`).
 *
 * @param authData - The raw `WWW-Authenticate` (Digest ...) header captured from a 401.
 * @returns The realm string, or an empty string when it cannot be parsed.
 */
export const parseDigestRealm = (authData?: string | null): string => {
	if (!authData) return '';
	try {
		const details = Object.fromEntries(
			authData.split(', ').map((v) => v.replace('Digest ', '').split('=')),
		);
		return details?.realm?.replace(/"/g, '') ?? '';
	} catch {
		return '';
	}
};

/**
 * Computes the Gen2+ digest HA1 hash the device stores/expects:
 * `ha1 = SHA256("admin:" + realm + ":" + password)`.
 *
 * This is the only credential representation we persist for Gen2+ devices. It is sufficient
 * for both `Shelly.SetAuth` and local digest auth (the digest `response` is derived from ha1),
 * and it is non-reversible to the user's reusable plaintext password.
 *
 * @param realm - The device realm (device id).
 * @param password - The plaintext passphrase entered by the user.
 * @returns The hex-encoded ha1 hash, or an empty string when inputs are missing.
 */
export const computeHa1 = (realm?: string | null, password?: string | null): string => {
	if (!realm || !password) return '';
	return sha256(`${DEVICE_AUTH_USER}:${realm}:${password}`).toString();
};

/**
 * Resolves the realm (device id) to use when computing ha1, preferring the authoritative
 * `DeviceInfo.id` and falling back to the realm embedded in a previously cached challenge.
 *
 * @param deviceInfoId - `settings.DeviceInfo.id` for the device, when available.
 * @param cachedAuthData - A previously cached `WWW-Authenticate` header, if any.
 * @returns The resolved realm, or an empty string when none can be determined.
 */
export const resolveDeviceRealm = (
	deviceInfoId?: string | null,
	cachedAuthData?: string | null,
): string => {
	if (deviceInfoId) return deviceInfoId;
	return parseDigestRealm(cachedAuthData);
};

/**
 * Verifies an entered passphrase against a cached ha1 by recomputing ha1 and comparing.
 * Pure local comparison — works on both cloud and local channels.
 *
 * @param realm - The device realm (device id).
 * @param password - The plaintext passphrase to verify.
 * @param cachedHa1 - The previously cached ha1 to compare against.
 * @returns True when the recomputed ha1 matches the cached ha1.
 */
export const verifyPassphrase = (
	realm: string | null | undefined,
	password: string,
	cachedHa1: string | null | undefined,
): boolean => {
	if (!cachedHa1) return false;
	const computed = computeHa1(realm, password);
	return computed !== '' && computed === cachedHa1;
};

/**
 * Migrates a single legacy Gen2+ `device_auths` entry stored as plaintext (`pass`)
 * into the hardened ha1 form (`ha1`), dropping the plaintext. Gen1 entries (Basic auth,
 * `{ user, pass }` with no `auth_data`) are intentionally left untouched.
 *
 * @param entry - The stored credential entry for a device.
 * @returns The migrated entry, or the original entry when no migration is needed/possible.
 */
export const migrateAuthEntryToHa1 = (entry: any): any => {
	if (!entry || typeof entry !== 'object') return entry;
	// Already hardened, or not a Gen2 digest entry (no challenge to derive the realm from).
	if (entry.ha1 || !entry.auth_data || !entry.pass) return entry;

	const realm = parseDigestRealm(entry.auth_data);
	const ha1 = computeHa1(realm, entry.pass);
	if (!ha1) return entry;

	const { pass, ...rest } = entry;
	return { ...rest, ha1 };
};

/**
 * Migrates an entire `device_auths` map from plaintext Gen2+ credentials to ha1.
 *
 * @param auths - The full credential map keyed by device id.
 * @returns A new map with all eligible Gen2+ entries hardened.
 */
export const migrateDeviceAuthsToHa1 = (
	auths: Record<string, any> | null | undefined,
): Record<string, any> => {
	if (!auths || typeof auths !== 'object') return auths ?? {};
	const next: Record<string, any> = {};
	for (const [id, entry] of Object.entries(auths)) {
		next[id] = migrateAuthEntryToHa1(entry);
	}
	return next;
};
